import { STATUS } from "@/constants/statuses";
import { WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES } from "@apps/wishlist/constants/constants";
import { applyTransaction } from "@datorama/akita";
import { arrayAdd } from "@datorama/akita";
import { ProductsService } from "@modules/products/services/ProductsService";
import { productsServiceToken } from "@modules/products/tokens";
import { catchError, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import {
    CreateGiftRegistryItemRequestBody,
    CreateGiftRegistryRequestBody,
    DeleteGiftRegistryRequestBody,
    GetSharedGiftRegistryParams,
    GiftRegistryApiService,
    MoveGiftRegistryItemRequestBody,
    UpdateGiftRegistryItemRequestBody,
    UpdateGiftRegistryRequestBody,
} from "./../api_services/GiftRegistryApiService";
import GiftCartStore from "../stores/GiftCartStore";
import GiftRegistryStore from "../stores/GiftRegistryStore";
import { giftRegistryApplicationLoggerToken } from "../tokens";

@singleton()
class GiftRegistryService {
    constructor(
        private readonly giftRegistryStore: GiftRegistryStore,
        private readonly giftCartStore: GiftCartStore,
        private readonly giftRegistryApiService: GiftRegistryApiService,
        @inject(productsServiceToken)
        private readonly productsService: ProductsService,
        @inject(giftRegistryApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    getGiftRegistriesWithItems() {
        this.logger.debug(
            "GiftRegistryService.getGiftRegistriesWithItems is started"
        );

        const status =
            this.giftRegistryStore.getStatus() === STATUS.VIRGIN
                ? STATUS.INITIAL_LOADING
                : STATUS.LOADING;

        this.giftRegistryStore.setStatus(status);

        return this.giftRegistryApiService.getGiftRegistriesWithItems().pipe(
            catchError((error: unknown) => {
                this.giftRegistryStore.setStatus(STATUS.ERROR);
                this.logger.debug(
                    "GiftRegistryService.getGiftRegistriesWithItems is errored",
                    { error }
                );
                throw error;
            }),
            tap((response) => {
                response.giftRegistriesWithItems.forEach((registry) => {
                    registry.items.forEach((registryItem) => {
                        this.productsService.addProductForFetching(
                            registryItem.productId
                        );
                    });
                });
            }),
            tap((response) => {
                applyTransaction(() => {
                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        registries: response.giftRegistriesWithItems,
                    }));

                    this.giftRegistryStore.setStatus(STATUS.READY);
                });
                this.logger.debug(
                    "GiftRegistryService.getGiftRegistriesWithItems is ready"
                );
            })
        );
    }

    getGiftRegistryTypes() {
        this.logger.debug(
            "GiftRegistryService.getGiftRegistryTypes is started"
        );

        return this.giftRegistryApiService.getGiftRegistryTypes().pipe(
            catchError((error: unknown) => {
                this.giftRegistryStore.setStatus(STATUS.ERROR);
                this.logger.debug(
                    "GiftRegistryService.getGiftRegistryTypes is errored",
                    { error }
                );
                throw error;
            }),
            tap((response) => {
                applyTransaction(() => {
                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        registryTypes: response.giftRegistryTypes,
                    }));
                });
                this.logger.debug(
                    "GiftRegistryService.getGiftRegistryTypes is ready"
                );
            })
        );
    }

    getGiftRegistryItems(queryParams: {
        giftRegistryId: number;
        offset?: number;
        perPage?: number;
    }) {
        this.logger.debug(
            "GiftRegistryService.getGiftRegistryItems is started"
        );
        this.giftRegistryStore.setLoadMoreStatus(STATUS.LOADING);

        return this.giftRegistryApiService
            .getGiftRegistryItems(queryParams)
            .pipe(
                catchError((error: unknown) => {
                    this.giftRegistryStore.setLoadMoreStatus(STATUS.ERROR);
                    this.logger.debug(
                        "GiftRegistryService.getGiftRegistryItems is errored",
                        { error }
                    );
                    throw error;
                }),
                tap((response) => {
                    response.items.forEach((item) => {
                        this.productsService.addProductForFetching(
                            item.giftRegistryItem.productId
                        );
                    });
                }),
                tap((data) => {
                    this.logger.debug(
                        "GiftRegistryService.getGiftRegistryItems is ready",
                        { data }
                    );

                    applyTransaction(() => {
                        this.giftRegistryStore.update((state) => ({
                            ...state,
                            registries: state.registries.map((registry) =>
                                registry.id === queryParams.giftRegistryId
                                    ? {
                                          ...registry,
                                          items: arrayAdd(
                                              registry.items,
                                              data.items.map(
                                                  (item) =>
                                                      item.giftRegistryItem
                                              )
                                          ),
                                          totalCount: data.totalCount,
                                          currentOffset: data.currentOffset,
                                      }
                                    : registry
                            ),
                        }));
                        this.giftRegistryStore.setLoadMoreStatus(STATUS.READY);
                    });
                })
            );
    }

    getCustomerAddresses() {
        this.logger.debug(
            "GiftRegistryService.getCustomerAddresses is started"
        );
        this.giftRegistryStore.setCustomerAddressesStatus(STATUS.LOADING);

        return this.giftRegistryApiService.getCustomerAddresses().pipe(
            catchError((error: unknown) => {
                this.giftRegistryStore.setCustomerAddressesStatus(STATUS.ERROR);
                this.logger.debug(
                    "GiftRegistryService.getCustomerAddresses is errored",
                    { error }
                );
                throw error;
            }),
            tap((response) => {
                applyTransaction(() => {
                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        customerAddresses: response.customerAddresses,
                    }));
                    this.giftRegistryStore.setCustomerAddressesStatus(
                        STATUS.READY
                    );
                });

                this.logger.debug(
                    "GiftRegistryService.getCustomerAddresses is ready"
                );
            })
        );
    }

    getSharedGiftRegistry(queryParams: GetSharedGiftRegistryParams) {
        this.logger.debug(
            "GiftRegistryService.getSharedGiftRegistry is started"
        );
        this.giftRegistryStore.setSharingStatus(STATUS.LOADING);

        return this.giftRegistryApiService
            .getSharedGiftRegistry(queryParams)
            .pipe(
                catchError((error: unknown) => {
                    applyTransaction(() => {
                        this.giftRegistryStore.setError(error);
                        this.giftRegistryStore.setSharingStatus(STATUS.ERROR);
                    });
                    this.logger.debug(
                        "GiftRegistryService.getSharedGiftRegistry is errored",
                        { error }
                    );
                    throw error;
                }),
                tap((response) => {
                    response.giftRegistryWithItems.items.forEach(
                        (registryItem) => {
                            this.productsService.addProductForFetching(
                                registryItem.productId
                            );
                        }
                    );
                }),
                tap((response) => {
                    applyTransaction(() => {
                        this.giftRegistryStore.update((state) => ({
                            ...state,
                            sharedRegistry: response.giftRegistryWithItems,
                        }));
                        this.giftRegistryStore.setSharingStatus(STATUS.READY);

                        this.giftCartStore.update((state) => ({
                            ...state,
                            registryId: response.giftRegistryWithItems.id,
                            registrant:
                                response.giftRegistryWithItems.registrant,
                        }));
                    });

                    this.logger.debug(
                        "GiftRegistryService.getSharedGiftRegistry is ready"
                    );
                })
            );
    }

    createGiftRegistry(data: CreateGiftRegistryRequestBody) {
        this.logger.debug("GiftRegistryService.createGiftRegistry is started");
        this.giftRegistryStore.setChangeRegistryStatus(STATUS.LOADING);

        return this.giftRegistryApiService.createGiftRegistry(data).pipe(
            catchError((error: unknown) => {
                applyTransaction(() => {
                    this.giftRegistryStore.setError(error);
                    this.giftRegistryStore.setChangeRegistryStatus(
                        STATUS.ERROR
                    );
                });
                this.logger.debug(
                    "GiftRegistryService.createGiftRegistry is errored",
                    { error }
                );
                throw error;
            }),
            tap((response) => {
                this.logger.debug(
                    "GiftRegistryService.createGiftRegistry is ready",
                    response
                );

                applyTransaction(() => {
                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        registries: [
                            {
                                ...response,
                                items: [],
                                perPage: 24,
                                totalCount: 0,
                                currentOffset: 0,
                            },
                            ...state.registries,
                        ],
                    }));
                    this.giftRegistryStore.setChangeRegistryStatus(
                        STATUS.READY
                    );
                });
            })
        );
    }

    deleteGiftRegistry(data: DeleteGiftRegistryRequestBody) {
        this.logger.debug("GiftRegistryService.deleteGiftRegistry is started");

        return this.giftRegistryApiService.deleteGiftRegistry(data).pipe(
            catchError((error: unknown) => {
                applyTransaction(() => {
                    this.giftRegistryStore.setError(error);
                });
                this.logger.debug(
                    "GiftRegistryService.deleteGiftRegistry is errored",
                    { error }
                );
                throw error;
            }),
            tap((deletedRegistry) => {
                this.giftRegistryStore.update((state) => ({
                    ...state,
                    registries: state.registries.filter(
                        (registry) => registry.id !== deletedRegistry.id
                    ),
                }));
                this.logger.debug(
                    "GiftRegistryService.deleteGiftRegistry is ready",
                    deletedRegistry
                );
            })
        );
    }

    updateGiftRegistry(data: UpdateGiftRegistryRequestBody) {
        this.logger.debug("GiftRegistryService.updateGiftRegistry is started");
        this.giftRegistryStore.setChangeRegistryStatus(STATUS.LOADING);

        return this.giftRegistryApiService.updateGiftRegistry(data).pipe(
            catchError((error: unknown) => {
                applyTransaction(() => {
                    this.giftRegistryStore.setError(error);
                    this.giftRegistryStore.setChangeRegistryStatus(
                        STATUS.ERROR
                    );
                });
                this.logger.debug(
                    "GiftRegistryService.updateGiftRegistry is errored",
                    { error }
                );
                throw error;
            }),
            tap((updatedRegistry) => {
                applyTransaction(() => {
                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        registries: state.registries.map((registry) =>
                            registry.id === updatedRegistry.id
                                ? {
                                      ...updatedRegistry,
                                      items: registry.items,
                                      perPage: registry.perPage,
                                      totalCount: registry.totalCount,
                                      currentOffset: registry.currentOffset,
                                  }
                                : registry
                        ),
                    }));

                    this.giftRegistryStore.setChangeRegistryStatus(
                        STATUS.READY
                    );
                });

                this.logger.debug(
                    "GiftRegistryService.updateGiftRegistry is ready",
                    updatedRegistry
                );
            })
        );
    }

    shareGiftRegistry(
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        giftRegistryId: number
    ) {
        this.logger.debug("GiftRegistryService.shareGiftRegistry is started");
        this.giftRegistryStore.setSharingStatus(STATUS.LOADING);

        return this.giftRegistryApiService
            .shareGiftRegistry(channelName, giftRegistryId)
            .pipe(
                catchError((error: unknown) => {
                    applyTransaction(() => {
                        this.giftRegistryStore.setError(error);
                        this.giftRegistryStore.setSharingStatus(STATUS.ERROR);
                    });
                    this.logger.debug(
                        "GiftRegistryService.shareGiftRegistry is errored",
                        { error }
                    );
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "GiftRegistryService.shareGiftRegistry is ready",
                        { channelName, giftRegistryId }
                    );
                    this.giftRegistryStore.setSharingStatus(STATUS.READY);
                })
            );
    }

    createGiftRegistryItem(data: CreateGiftRegistryItemRequestBody) {
        this.logger.debug(
            "GiftRegistryService.createGiftRegistryItem is started"
        );

        applyTransaction(() => {
            this.giftRegistryStore.setItemLoadingInRegistry(
                data.productId,
                data.giftRegistryId
            );
        });

        return this.giftRegistryApiService.createGiftRegistryItem(data).pipe(
            catchError((error: unknown) => {
                applyTransaction(() => {
                    this.giftRegistryStore.setError(error);
                    this.giftRegistryStore.unSetItemLoadingInRegistry(
                        data.productId,
                        data.giftRegistryId
                    );
                });
                this.logger.debug(
                    "GiftRegistryService.createGiftRegistryItem is errored",
                    { error }
                );
                throw error;
            }),
            tap((createdItem) => {
                applyTransaction(() => {
                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        registries: state.registries.map((registry) =>
                            registry.id === data.giftRegistryId
                                ? {
                                      ...registry,
                                      items: [createdItem, ...registry.items],
                                  }
                                : registry
                        ),
                    }));
                    this.giftRegistryStore.unSetItemLoadingInRegistry(
                        data.productId,
                        data.giftRegistryId
                    );
                });
                this.logger.debug(
                    "GiftRegistryService.createGiftRegistryItem is ready",
                    data
                );
            })
        );
    }
    deleteGiftRegistryItem(data: {
        productId: number;
        itemId: number;
        registryId: number;
    }) {
        this.logger.debug(
            "GiftRegistryService.deleteGiftRegistryItem is started"
        );

        this.giftRegistryStore.setItemLoadingInRegistry(
            data.productId,
            data.registryId
        );

        return this.giftRegistryApiService
            .deleteGiftRegistryItem({ giftRegistryItemId: data.itemId })
            .pipe(
                catchError((error: unknown) => {
                    applyTransaction(() => {
                        this.giftRegistryStore.setError(error);
                        this.giftRegistryStore.unSetItemLoadingInRegistry(
                            data.productId,
                            data.registryId
                        );
                    });
                    this.logger.debug(
                        "GiftRegistryService.deleteGiftRegistryItem is errored",
                        { error }
                    );
                    throw error;
                }),
                tap((deletedItem) => {
                    applyTransaction(() => {
                        this.giftRegistryStore.update((state) => ({
                            ...state,
                            registries: state.registries.map((registry) =>
                                registry.id === data.registryId
                                    ? {
                                          ...registry,
                                          items: registry.items.filter(
                                              (item) =>
                                                  item.id !== deletedItem.id
                                          ),
                                      }
                                    : registry
                            ),
                        }));
                        this.giftRegistryStore.unSetItemLoadingInRegistry(
                            data.productId,
                            data.registryId
                        );
                    });

                    this.logger.debug(
                        "GiftRegistryService.deleteGiftRegistryItem is ready",
                        data
                    );
                })
            );
    }

    moveGiftRegistryItem(data: MoveGiftRegistryItemRequestBody) {
        this.logger.debug(
            "GiftRegistryService.moveGiftRegistryItem is started"
        );

        return this.giftRegistryApiService.moveGiftRegistryItem(data).pipe(
            catchError((error: unknown) => {
                applyTransaction(() => {
                    this.giftRegistryStore.setError(error);
                });
                this.logger.debug(
                    "GiftRegistryService.moveGiftRegistryItem is errored",
                    { error }
                );
                throw error;
            }),
            tap((movedItem) => {
                applyTransaction(() => {
                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        registries: state.registries.map((registry) =>
                            registry.id === data.fromGiftRegistryId
                                ? {
                                      ...registry,
                                      items: registry.items.filter(
                                          (item) => item.id !== movedItem.id
                                      ),
                                  }
                                : registry
                        ),
                    }));

                    this.giftRegistryStore.update((state) => ({
                        ...state,
                        registries: state.registries.map((registry) =>
                            registry.id === data.toGiftRegistryId
                                ? {
                                      ...registry,
                                      items: [...registry.items, movedItem],
                                  }
                                : registry
                        ),
                    }));
                });

                this.logger.debug(
                    "GiftRegistryService.moveGiftRegistryItem is ready"
                );
            })
        );
    }

    updateGiftRegistryItem(data: UpdateGiftRegistryItemRequestBody) {
        this.logger.debug(
            "GiftRegistryService.updateGiftRegistryItem is started"
        );

        return this.giftRegistryApiService.updateGiftRegistryItem(data).pipe(
            catchError((error: unknown) => {
                applyTransaction(() => {
                    this.giftRegistryStore.setError(error);
                });
                this.logger.debug(
                    "GiftRegistryService.updateGiftRegistryItem is errored",
                    { error }
                );
                throw error;
            }),
            tap((updatedItem) => {
                this.giftRegistryStore.update((state) => ({
                    ...state,
                    registries: state.registries.map((registry) =>
                        registry.id === data.giftRegistryItem.giftRegistryId
                            ? {
                                  ...registry,
                                  items: registry.items.map((item) =>
                                      item.id === data.giftRegistryItem.id
                                          ? updatedItem
                                          : item
                                  ),
                              }
                            : registry
                    ),
                }));
            })
        );
    }
}

export default GiftRegistryService;
